import React, { useEffect } from 'react'
import clsx from 'clsx'
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell,
  TableContainer, TableBody, useTheme
} from '@mui/material'
import { grey, red } from '@mui/material/colors'
import { isValidNumberString } from '../../../utils/string-utils'
import { type RiskLiquidation } from '../../../models/risk/risk-liquidation'
import { useSpring, animated } from '@react-spring/web'

const percentageFormatter = new Intl.NumberFormat('en-US',
  {
    style: 'percent'
  })

const RiskCard: React.FunctionComponent<RiskLiquidation> = (props: RiskLiquidation) => {
  const owner = props.owner
  const symbol = props.symbol_name
  const marginRatio = isValidNumberString(props.margin_ratio) ? props.margin_ratio : '-'
  const isPM = props.is_pm?.toLowerCase() === 'true' || props.is_classicpm?.toLowerCase() === 'true'
  const portfolioMargin = isValidNumberString(props.portfolio_margin) ? props.portfolio_margin : '-'

  const isMarginRatioCritical = !isPM && marginRatio !== '-' && Math.abs(Number(marginRatio)) <= 0.17
  const isPortfolioMarginRatioCritical = isPM && portfolioMargin !== '-' && (Number(portfolioMargin) >= -2.0 && Number(portfolioMargin) <= 2.0)

  const theme = useTheme()
  const [springs, api] = useSpring(() => ({
    from: {
      border: '8px solid ' + theme.palette.background.paper
    }
  }))
  const showRatio = (props.show_ratio as boolean) ?? true

  useEffect(() => {
    if (isMarginRatioCritical || isPortfolioMarginRatioCritical) {
      api.start({
        from: {
          border: '8px solid ' + theme.palette.background.paper
        },
        to: {
          border: '8px solid ' + red[900]
        },
        loop: true,
        config: { tension: 120, friction: 14 }
      })
    }
  }, [])

  return (
    <Card variant="outlined">
      <animated.div style={{ ...springs }}>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& .text.inactive': {
                      color: grey[50]
                    }
                  }}
                >
                  <TableCell align="center">Symbol</TableCell>
                  <TableCell align="center">Account</TableCell>
                  {showRatio
                    ? <TableCell align="right" className={clsx('text', { inactive: isPM })}>Ratio</TableCell>
                    : <TableCell align="right" className={clsx('text', { inactive: !isPM })}>P.Margin</TableCell>
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{
                    '& .text.positive': {
                      color: 'success.light'
                    },
                    '& .text.negative': {
                      color: 'error.light'
                    },
                    '& .text.inactive': {
                      color: grey[50]
                    }
                  }}
                >
                  <TableCell align="right">
                    {symbol}
                  </TableCell>
                  <TableCell align="right">
                    {owner}
                  </TableCell>
                  {showRatio
                    ? <TableCell
                        align="right"
                        sx={{
                          fontSize: 'large'
                        }}
                        className={clsx('text', {
                          positive: marginRatio !== '-' && Number(marginRatio) > 0,
                          negative: marginRatio !== '-' && Number(marginRatio) < 0,
                          inactive: isPM
                        })}>
                        {marginRatio !== '-' ? percentageFormatter.format(Number(marginRatio)) : marginRatio}
                      </TableCell>
                    : <TableCell
                        align="right"
                        sx={{
                          fontSize: 'large'
                        }}
                        className={clsx('text', {
                          positive: portfolioMargin !== '-' && Number(portfolioMargin) > 0,
                          negative: portfolioMargin !== '-' && Number(portfolioMargin) < 0,
                          inactive: !isPM
                        })}>
                        {portfolioMargin !== '-' ? percentageFormatter.format(Number(portfolioMargin)) : portfolioMargin}
                      </TableCell>
                  }
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </animated.div>
    </Card>
  )
}

export default RiskCard
